/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as yup from 'yup';
import * as Message from '../../util/messageValidation';
import { isNullOrEmptyArray } from '../../util/arrays';
import { uploadFile, markAsCompleted } from '../../stores/actions/upload';
import Dropzone from './Dropzone';
import GoogleDriveInput from './GoogleDriveInput';
import DropboxInput from './DropboxInput';
import * as Ui from './styles';
import { dateFormat } from '../../util/formatters/dateFormat';
import {normalizeUploadedFile} from "src/util/csv";

import { Selector, Modal, InputDate } from '../../components';
import useFlashMessage from 'src/hooks/FlashMessage';
import { parseUploadErrors } from './util/parseErrors';
import { exportErrorsToExcel } from './util/exportErrorsExcel';
import XlsxFile from 'src/components/Icons/XlsxFile';
import { DownloadIcon } from 'src/components/Icons';
import TooltipInfo from 'src/components/TooltipInfo';
import {
  ERROR_MESSAGE,
  FILE_DOC_LINK,
  FILE_MODEL_LINK,
  FILE_MODEL_TOOLTIP_TEXT,
  SUCCESS_MESSAGE,
} from './constants';

/**
 * Create validation scheme
 */
const schema = yup.object().shape({
  titulo: yup
    .string()
    .min(5, 'Este campo deve possuir no mínimo 5 caracteres')
    .max(250, 'Este campo deve possuir no máximo 250 caracteres')
    .required(Message.required),
  clientes: yup.array().min(1, 'Campo obrigatório'),
  pesquisaDataInicio: yup.date().typeError('O campo deve possuir uma data válida').required(Message.required),
  pesquisaDataFinalizacao: yup.date().typeError('O campo deve possuir uma data válida').required(Message.required)
    .min(yup.ref('pesquisaDataInicio', ''), 'A data de finalização deve ser maior que a data de início da pesquisa'),
});

/**
 * Upload Survey File Section (Modal)
 */
function UploadSurvey(props) {
  // props
  const { tenants, isOpen, onClose } = props;
  // from redux
  const { dispatch } = props;
  // from redux state
  const { running, uploadProps } = props;
  const { uploadProgress, uploadIsCompleted, uploadError } = uploadProps;

  // state
  const [file, setFile] = useState(null);
  const [uploadHasError, setUploadHasError] = useState(false);
  const [errorsList, setErrorsList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pesquisaDataInicio, setPesquisaDataInicio] = useState();
  const [pesquisaDataFinalizacao, setPesquisaDataFinalizacao] = useState();
  const [showFlashMessage] = useFlashMessage();

  // refs
  const formRef = useRef();

  const closeForm = useCallback(() => {
    setFile(null);
    setUploadHasError(false);
    setErrorsList([]);
    if (onClose) onClose();
  }, [onClose]);

  // initial values for formik
  const initialValues = {
    arquivo: [],
    clientes: [],
    tipoImportacao: 'ARQUIVO_AVULSO',
    tipoArquivoImportacao: 'CSV',
    titulo: '',
    pesquisaDataInicio,
    pesquisaDataFinalizacao,
  };

  // handle formik submit
  function handleSubmit(values) {
    if (file) {
      if (!formRef.current.isValid) {
        return;
      }
      setIsLoading(true);
      uploadFile({
        tenants: values.clientes,
        title: values.titulo,
        type: values.tipoImportacao,
        fileType: values.tipoArquivoImportacao,
        file,
        pesquisaDataInicio: pesquisaDataInicio ? dateFormat(pesquisaDataInicio) : '',
        pesquisaDataFinalizacao: pesquisaDataFinalizacao ? dateFormat(pesquisaDataFinalizacao) : '',
      }, dispatch);
    } else {
      toast.error('É necessário a inserção de um arquivo CSV.');
    }
  }

  // handle reset state
  function handleFormReset() {
    closeForm();
  }

  // handle accepted file
  async function handleAcceptFile(acceptedFile) {
    const fileToAccept = acceptedFile && acceptedFile.length > 0 ? acceptedFile[0] : acceptedFile;

    if (fileToAccept) {
      try {
        const normalizedFile = await normalizeUploadedFile(fileToAccept);
        setFile(normalizedFile);
      } catch (error) {
        toast.error('Erro ao processar arquivo CSV');
        setFile(null);
      }
    } else {
      setFile(null);
    }
  }

  function handleGoogleDriveFile(loadedFile) {
    setFile(loadedFile);
    setUploadHasError(false);
    setErrorsList([]);
  }

  // handle change options
  function handleDropboxFile(loadedFile) {
    setFile(loadedFile);
    setUploadHasError(false);
    setErrorsList([]);
  }

  function handleChangeOptionTenants(setFieldValue, options) {
    // does nothing
    if (isNullOrEmptyArray(options)) return;
    // first value
    setFieldValue(
      'clientes',
      options.map((option) => option.value),
    );
  }

  // força o formik a submeter o envio
  function handleFormSubmit() {
    // Verifica se o form é valido, para alterar comportamento de carregamento
    formRef.current.submitForm();
  }

  // altera o valor da data inicial
  function handleChangeInitialDate(setFieldValue, date) {
    setFieldValue(
      'pesquisaDataInicio',
      date,
    );
    setPesquisaDataInicio(date);
  }
  // altera o valor da data de finalização
  function handleChangeEndDate(setFieldValue, date) {
    setFieldValue(
      'pesquisaDataFinalizacao',
      date,
    );
    setPesquisaDataFinalizacao(date);
  }

  const showUploadErrorMessages = useCallback((error) => {
    if (error) {
      setUploadHasError(true);
      setIsLoading(false);

      const body = error.response?.data?.body;
      const fieldErrors = body?.field_errors;
      const generalErrors = body?.generalErrors;

      if (!isNullOrEmptyArray(generalErrors)) {
        showFlashMessage(
          ERROR_MESSAGE.title,
          ERROR_MESSAGE.message,
          "error"
        );
      }

      if (!isNullOrEmptyArray(fieldErrors) && fieldErrors[0]?.message) {
        const mapped = parseUploadErrors(fieldErrors[0].message);
        setErrorsList(mapped);
      }

      markAsCompleted(dispatch);
    }
  }, [dispatch, showFlashMessage]);

  // efeito colateral após o upload com sucesso
  useEffect(() => {
    if (uploadIsCompleted) {
      closeForm();
      showFlashMessage(
        SUCCESS_MESSAGE.title,
        SUCCESS_MESSAGE.message,
        "success"
      );
      setUploadHasError(false)
      setErrorsList([]);

      markAsCompleted(dispatch);
    }
  }, [uploadIsCompleted, closeForm, dispatch]);

  // efeito colateral após o upload com falha
  useEffect(() => {
    showUploadErrorMessages(uploadError);
  }, [showUploadErrorMessages, uploadError]);

  // efeito colateral ao receber os tenants
  useEffect(() => {
    setCustomers(
      tenants.map((item) => ({ label: item.nome, value: item.id })),
    );
  }, [tenants]);

  // Renderiza os três primeiros erros do errorsList
  const renderUploadErrors = () =>
    errorsList.slice(0, 3).map((err, index) => {
      const hasLinha = err.linha !== null;
      const hasColuna = err.coluna !== null;

      const msg =
        hasLinha && hasColuna
          ? `Linha ${err.linha} | Coluna ${err.coluna} – ${err.mensagem}`
        : hasColuna
          ? `Coluna ${err.coluna} – ${err.mensagem}`
        : `${err.mensagem}`;

      return (
        <Ui.UploadErrorMessage key={index}>
          • {msg}
        </Ui.UploadErrorMessage>
      );
  });

  return (
    <Ui.Container>
      <Modal
        hideTitle
        isOpen={isOpen}
        onOk={handleFormSubmit}
        onCancel={handleFormReset}
        okBtnTitle={'Adicionar arquivo'}
        cancelBtnTitle={'Cancelar'}
        showActions={!isLoading}
        contentHeight={'636px'}
      >
        <Formik
          onSubmit={handleSubmit}
          innerRef={formRef}
          initialValues={initialValues}
          validationSchema={schema}
        >
          {(childrenProps) => {
          const { setFieldValue, errors, touched } = childrenProps;
          const tituloHasError = errors.titulo && touched.titulo;
          const clientesHasError = errors.clientes && touched.clientes;
          const dataInicialHasError = errors.pesquisaDataInicio && touched.pesquisaDataInicio;
          const dataFinalHasError = errors.pesquisaDataFinalizacao && touched.pesquisaDataFinalizacao;

            return (
              <Form encType="multipart/form-data" style={{ width: '100%' }}>

                <Ui.ContentWrapper>
                  <Ui.PageTitle>Identifique sua avaliação</Ui.PageTitle>
                  <Ui.FieldWrapper hasError={tituloHasError}>
                    <Ui.InputLabel>Título da avaliação*</Ui.InputLabel>
                    <Field
                      name="titulo"
                      type="text"
                      placeholder="Ex.: Levantamento de Opinião Pública – Abril"
                      disabled={isLoading}
                      className="inputText"
                      maxLength={251}
                    />

                    <ErrorMessage name="titulo">
                      {(msg) => <span className="errorMessage">{msg}</span>}
                    </ErrorMessage>
                  </Ui.FieldWrapper>
                  <Ui.GroupInputs>
                    <Ui.DateContainer hasError={dataInicialHasError}>
                      <Ui.InputLabel>Data de início *</Ui.InputLabel>
                        <InputDate
                          placeholder={'30/11/2024'}
                          value={pesquisaDataInicio}
                          inputName="pesquisaDataInicio"
                          onChangeDate={(date) => handleChangeInitialDate(setFieldValue, date)}
                        />

                      <ErrorMessage name="pesquisaDataInicio">
                        {(msg) => <span className="errorMessage">{msg}</span>}
                      </ErrorMessage>
                    </Ui.DateContainer>
                    <Ui.DateContainer hasError={dataFinalHasError}>
                      <Ui.InputLabel>Data de finalização *</Ui.InputLabel>
                        <InputDate
                          placeholder={'30/11/2024'}
                          value={pesquisaDataFinalizacao}
                          inputName="pesquisaDataFinalizacao"
                          onChangeDate={(date) => handleChangeEndDate(setFieldValue, date)}
                        />

                      <ErrorMessage name="pesquisaDataFinalizacao">
                        {(msg) => <span className="errorMessage">{msg}</span>}
                      </ErrorMessage>
                    </Ui.DateContainer>
                  </Ui.GroupInputs>

                  <Ui.SelectorWrapper hasError={clientesHasError}>
                    <Ui.InputLabel>Selecione o(s) cliente(s)*</Ui.InputLabel>

                    <Selector
                      disabled={isLoading}
                      inputName="clientes"
                      type="multi"
                      placeholder="Selecione o(s) cliente(s)"
                      data={customers}
                      isLoading={running}
                      onChange={(options) => handleChangeOptionTenants(setFieldValue, options)}
                      enableFilter
                      filterWithDarkInput={true}
                      className={'custom-selector'}
                    />

                    <ErrorMessage name="clientes">
                      {(msg) => <span className="errorMessage">{msg}</span>}
                    </ErrorMessage>
                  </Ui.SelectorWrapper>

                  <Ui.DropzoneWrapper>
                    <Ui.PageTitleWrapper>
                      <Ui.PageTitle>Upload e validação do arquivo</Ui.PageTitle>
                      <TooltipInfo
                        toolTipText={FILE_MODEL_TOOLTIP_TEXT}
                        iconColor={'#0D9B9E'}
                      />
                    </Ui.PageTitleWrapper>

                    <Ui.FilesWrapper>
                      <Ui.ModelFileWrapper download href={FILE_MODEL_LINK} target='_blank'>
                        <DownloadIcon />
                        <Ui.ModelFileTitle>Baixar Modelo de arquivo</Ui.ModelFileTitle>
                      </Ui.ModelFileWrapper>

                      <span className='pipe'>|</span>

                      <Ui.ModelFileWrapper download href={FILE_DOC_LINK} target='_blank'>
                        <DownloadIcon />
                        <Ui.ModelFileTitle>Documentação de apoio</Ui.ModelFileTitle>
                      </Ui.ModelFileWrapper>
                    </Ui.FilesWrapper>

                    <Ui.UploadOptionsRow>
                      <Ui.DropzoneColumn>
                        <Dropzone
                          disabled={isLoading}
                          file={file}
                          onAcceptFile={handleAcceptFile}
                          progress={uploadProgress}
                          hasError={uploadHasError}
                          onClearError={() => setUploadHasError(false)}
                          compact
                        />
                      </Ui.DropzoneColumn>

                      <Ui.OrDivider>
                        <Ui.OrText>Ou</Ui.OrText>
                      </Ui.OrDivider>

                      <GoogleDriveInput
                        onFileLoaded={handleGoogleDriveFile}
                        disabled={isLoading}
                        hasError={uploadHasError}
                      />

                      <Ui.OrDivider>
                        <Ui.OrText>Ou</Ui.OrText>
                      </Ui.OrDivider>

                      <DropboxInput
                        onFileLoaded={handleDropboxFile}
                        disabled={isLoading}
                        hasError={uploadHasError}
                      />
                    </Ui.UploadOptionsRow>

                    {uploadHasError && (
                      <span className="errorMessage">
                        A importação não foi concluída. Encontramos alguns problemas no arquivo. Corrija os pontos abaixo e tente novamente.
                      </span>
                    )}
                  </Ui.DropzoneWrapper>
                  {uploadHasError && 
                    <Ui.UploadErrorMessageWrapper>
                      {renderUploadErrors()}
                    </Ui.UploadErrorMessageWrapper>
                  }

                  {errorsList.length > 3 && (
                    <Ui.DownloadErrorsWrapper onClick={() => exportErrorsToExcel(errorsList)}>
                        <XlsxFile className="xlsx-icon" />
                        <Ui.DownloadErrorsText>Baixar relatório completo</Ui.DownloadErrorsText>
                    </Ui.DownloadErrorsWrapper>
                  )}
                  <Ui.LineSeparator />
                  {isLoading ? <span className="upload-tip">Seu arquivo está sendo carregado. Isso pode levar alguns segundos.</span> : ''}
                </Ui.ContentWrapper>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </Ui.Container>
  );
}

// [required]
const mapStateToProps = (state) => {
  const uploadState = state.upload.payload;
  const uploadProps = {
    uploadProgress: uploadState.progress,
    uploadIsCompleted: uploadState.isCompleted,
    uploadError: state.upload.error,
  };
  return { uploadProps };
};

export default connect(mapStateToProps)(UploadSurvey);
